import {cn} from "@adleritech/web-ui/utils";
import {useCallback, useRef, useState, useSyncExternalStore} from "react";
import styles from "./ScrollFadeWrapper.module.css";

type ScrollFadeWrapperProps = React.HTMLAttributes<HTMLDivElement> & {
    children: React.ReactNode;
    className?: string;
};

type ScrollState = {canScrollUp: boolean; canScrollDown: boolean};

function measureScroll(el: HTMLElement | null): ScrollState {
    if (!el) return {canScrollUp: false, canScrollDown: false};
    const hasMoreContent = el.scrollHeight > el.clientHeight;
    const isAtTop = el.scrollTop <= 1;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    return {
        canScrollUp: hasMoreContent && !isAtTop,
        canScrollDown: hasMoreContent && !isAtBottom,
    };
}

export function ScrollFadeWrapper({
    children,
    className,
    onScroll,
    ...props
}: ScrollFadeWrapperProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [scrollState, setScrollState] = useState<ScrollState>({
        canScrollUp: false,
        canScrollDown: false,
    });

    // Must be stable for useSyncExternalStore subscribe callback
    const updateScrollState = useCallback(() => {
        const newState = measureScroll(wrapperRef.current);
        setScrollState((prev) => {
            // Only update if values actually changed
            if (
                prev.canScrollUp === newState.canScrollUp &&
                prev.canScrollDown === newState.canScrollDown
            ) {
                return prev;
            }
            return newState;
        });
    }, []);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        updateScrollState();
        onScroll?.(event);
    };

    // useSyncExternalStore requires stable subscribe callback - React Compiler cannot auto-memoize it
    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            const el = wrapperRef.current;
            if (!el) return () => {};

            const handleChange = () => {
                updateScrollState();
                onStoreChange();
            };

            const resizeObserver = new ResizeObserver(handleChange);
            resizeObserver.observe(el);
            const observedChildren = new Set<Element>();
            Array.from(el.children).forEach((child) => {
                resizeObserver.observe(child);
                observedChildren.add(child);
            });

            const mutationObserver = new MutationObserver((mutations) => {
                handleChange();
                for (const mutation of mutations) {
                    for (const node of mutation.removedNodes) {
                        if (node instanceof Element && observedChildren.has(node)) {
                            resizeObserver.unobserve(node);
                            observedChildren.delete(node);
                        }
                    }
                    for (const node of mutation.addedNodes) {
                        if (node instanceof Element && !observedChildren.has(node)) {
                            resizeObserver.observe(node);
                            observedChildren.add(node);
                        }
                    }
                }
            });
            mutationObserver.observe(el, {childList: true, subtree: true});

            // Initial measurement
            handleChange();

            return () => {
                resizeObserver.disconnect();
                mutationObserver.disconnect();
            };
        },
        [updateScrollState]
    );

    useSyncExternalStore(
        subscribe,
        () => scrollState,
        () => ({canScrollUp: false, canScrollDown: false})
    );

    return (
        <div
            ref={wrapperRef}
            className={cn(styles.wrapper, className)}
            onScroll={handleScroll}
            {...props}
        >
            <div className={cn(styles.fadeTop, scrollState.canScrollUp && styles.visible)} />
            {children}
            <div className={cn(styles.fadeBottom, scrollState.canScrollDown && styles.visible)} />
        </div>
    );
}
